import {
  useBusinessEntity,
  useCapTableTransactions,
  usePersonsInEntity,
  useStockClasses,
} from '@commenda/api';
import { getCorporationName, getPrintableDirectorName } from '@commenda/utils';
import { StakeholderType } from '@prisma/client';

export const useOwnershipInfo = ({
  businessEntityId,
}: {
  businessEntityId: number;
}) => {
  const { data: businessEntity, error: businessEntitiesError } =
    useBusinessEntity(businessEntityId);

  const { data: capTableTransactions, error: capTableTransactionsError } =
    useCapTableTransactions(businessEntityId);

  const { data: personsInEntity, error: personsInEntityError } =
    usePersonsInEntity(businessEntityId);

  const { data: stockClasses, error: stockClassesError } =
    useStockClasses(businessEntityId);

  const stockClass = stockClasses?.[0];

  // Use OWNERSHIP_MODEL if we have cap table data available, otherwise fall back to OLD_CORPORATION_MODEL
  const ownershipSource: 'OWNERSHIP_MODEL' | 'OLD_CORPORATION_MODEL' =
    stockClass && capTableTransactions && capTableTransactions.length > 0
      ? 'OWNERSHIP_MODEL'
      : 'OLD_CORPORATION_MODEL';

  const isLoading =
    !businessEntity &&
    (ownershipSource === 'OWNERSHIP_MODEL'
      ? !stockClass || !capTableTransactions
      : !personsInEntity);

  const error =
    businessEntitiesError ||
    capTableTransactionsError ||
    personsInEntityError ||
    stockClassesError;

  const totalAuthorizedShares =
    ownershipSource === 'OWNERSHIP_MODEL'
      ? stockClass?.authorizedShares
      : businessEntity?.structure?.authorizedShares;

  const outstandingShares =
    ownershipSource === 'OWNERSHIP_MODEL'
      ? stockClass?.outstandingShares
      : (businessEntity?.structure?.authorizedShares || 0) -
        ((businessEntity?.structure?.authorizedShares || 0) *
          (businessEntity?.structure?.esopSharePercentage || 0)) /
          100;

  const esopPercentage =
    ownershipSource === 'OWNERSHIP_MODEL'
      ? (((stockClass?.authorizedShares || 0) -
          (stockClass?.outstandingShares || 0)) /
          (stockClass?.authorizedShares || 1)) *
        100
      : businessEntity?.structure?.esopSharePercentage;

  const shareholdersInformation: {
    name: string;
    email: string | null;
    noOfShares: number;
    percentage: number;
  }[] = [];

  if (ownershipSource === 'OWNERSHIP_MODEL') {
    capTableTransactions?.forEach((transaction) => {
      const { stakeholder } = transaction.stakeholding;
      const stakeholderType = stakeholder.type;

      let name = 'Unknown';
      if (stakeholderType === StakeholderType.INDIVIDUAL) {
        if (stakeholder.director) {
          name = getPrintableDirectorName(stakeholder.director);
          if (!name)
            name =
              `${stakeholder.director.directorFirstName || ''} ${stakeholder.director.directorLastName || ''}`.trim() ||
              'Unknown Individual';
        }
      } else if (stakeholder.corporation) {
        name = getCorporationName(stakeholder.corporation);
        if (!name)
          name =
            stakeholder.corporation.companyLegalName || 'Unknown Corporation';
      }

      shareholdersInformation.push({
        name,
        email:
          stakeholderType === StakeholderType.INDIVIDUAL
            ? stakeholder.director?.email
            : stakeholder.corporation?.contactEmail || null,
        noOfShares: transaction.additionalInformation?.quantity || 0,
        percentage:
          ((transaction.additionalInformation?.quantity || 0) /
            (stockClass?.authorizedShares || 1)) *
          100,
      });
    });
  } else {
    personsInEntity?.persons?.forEach((person) => {
      shareholdersInformation.push({
        name: [person.firstName, person.lastName].join(' '),
        email: person.email,
        noOfShares:
          (person.sharePercentage *
            (businessEntity?.structure?.authorizedShares || 0)) /
          100,
        percentage: person.sharePercentage,
      });
    });
  }

  const isCapTablePopulated =
    ownershipSource === 'OLD_CORPORATION_MODEL'
      ? !!businessEntity?.structure?.initialSharePrice
      : !!capTableTransactions?.length;

  return {
    isLoading,
    error,
    shareClassInformation: {
      totalAuthorizedShares,
      outstandingShares,
      esopPercentage,
    },
    shareholdersInformation,
    businessEntity: {
      id: businessEntity?.id,
      name: businessEntity?.generalCorporationInfo?.legalName,
      businessEntityType:
        businessEntity?.generalCorporationInfo?.businessEntityType,
      isIncorporationInProgress:
        !!businessEntity?.featureSpecificInfo?.incorporation
          ?.isIncorporationInProgress,
      companyId: businessEntity?.generalCorporationInfo?.companyId,
    },
    isCapTablePopulated,
  };
};
